/**
 * SoD state + alternatives for an item the viewer prepared — V98-3-5
 * (ADR-0046 T6, T8–T10; research §2 rules 10, 16, 18).
 *
 * The routing state is shown as a state ("Routed · with Grace · you
 * prepared this", or "Nobody else eligible"). When nobody is eligible the
 * alternatives follow in order; the compensating review stays locked until
 * the server unlocks it and is never the primary action.
 *
 * The compensating review (V98-3 review S7 + U9) is TWO steps in one dialog
 * and ONE write:
 *   1. Facts + alternatives tried → POST …/dry-run (writes nothing): the
 *      facts rule, alternatives and T10 checker eligibility come back first.
 *   2. Who checks it and by when → POST …/submit, once per item. Nobody
 *      eligible → an inline independent party (no record is created); a
 *      review already pending for the item is resumed, never duplicated.
 * Step 2 takes focus through its step line; errors live inside the dialog,
 * the alternatives error is attached to its fieldset, every error clears on
 * change. A batch (one-approver orgs) covers signatures only and says how
 * many (U8).
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Field, Input, Select, Textarea } from '@aigovops/design-system';
import {
  addIndependentParty, askToDelegate, askToSignRemote, compensatingDryRun, getAlternatives, holdItem, submitCompensating,
  type CheckerOptions, type CompensatingDryRun, type CoverageItemKind,
} from '../../lib/coverage-api';
import { CHECK_MAX_DAYS, FACTS_MIN, alternativeSteps, alternativesTried, checkerWhy, type AltStep } from '../../lib/item-model';
import { fmtDay, nameOf, type Names } from '../../lib/today-model';
import { AskAdmin } from '../AskAdmin';
import { ActionDialog, Chip, errorText } from '../today/shared';

const STATUS_CHIP: Record<AltStep['status'], { text: string; tone: 'ok' | 'amber' | 'lock' | 'neutral' | 'outline' }> = {
  available: { text: 'Available', tone: 'ok' },
  todo: { text: 'Try this', tone: 'outline' },
  waiting: { text: 'Waiting', tone: 'neutral' },
  tried: { text: 'Tried', tone: 'lock' },
  fails: { text: 'Misses the hard date', tone: 'amber' },
  na: { text: 'Not applicable', tone: 'lock' },
  locked: { text: 'Locked', tone: 'lock' },
  unlocked: { text: 'Unlocked', tone: 'amber' },
};

export function SodPanel({ slug, kind, id, routed, routedTo, names, batchIds, onDone, onAddAdvisor }: {
  slug: string; kind: CoverageItemKind; id: string;
  /** Routed to someone else (state only), vs none eligible (alternatives). */
  routed: boolean; routedTo: string | null; names: Names;
  /** One-approver orgs: record several compensating reviews in one batch (signatures only). */
  batchIds?: string[];
  onDone: (msg: string, focusId: string) => void;
  /** Owner/admin: the primary fix is an advisor seat (rule 15). Others ask an admin. */
  onAddAdvisor?: (opener: HTMLElement) => void;
}) {
  const qc = useQueryClient();
  const alt = useQuery({ queryKey: ['alternatives', slug, kind, id], queryFn: () => getAlternatives(slug, kind, id), enabled: !routed, retry: false });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [compOpen, setCompOpen] = useState(false);

  if (routed) {
    return (
      <div className="gp-state gp-state--ok" id="sod-state" tabIndex={-1}>
        <strong>{`Routed · with ${nameOf(names, routedTo, 'another approver')}`}</strong>
      </div>
    );
  }
  if (alt.isLoading) return <p className="gp-dim">Checking who else can approve…</p>;
  if (alt.error || !alt.data) return <Alert variant="error">The options could not load.</Alert>;
  const a = alt.data.alternatives;
  const steps = alternativeSteps(a, names);

  const run = async (key: string, fn: () => Promise<unknown>, msg: string) => {
    setBusy(key); setError(null);
    try { await fn(); await qc.invalidateQueries({ queryKey: ['alternatives', slug, kind, id] }); await qc.invalidateQueries({ queryKey: ['today', slug] }); onDone(msg, `alt-${key}`); }
    catch (e) { setError(errorText(e, 'That did not go through.')); }
    finally { setBusy(null); }
  };
  const action = (s: AltStep) => {
    if (s.id === 'ask_delegate' && s.status === 'todo') {
      const remote = a.options.ask_delegate.ask_kind === 'sign_remote' && kind === 'signature_request';
      return <Button compact aria-disabled={!!busy || undefined} onClick={() => void run('ask_delegate',
        () => (remote ? askToSignRemote(slug, { item_id: id }) : askToDelegate(slug, { item_kind: kind, item_id: id })), 'Ask sent')}>{s.title}</Button>;
    }
    if (s.id === 'hold' && s.status === 'todo') {
      return <Button compact aria-disabled={!!busy || undefined} onClick={() => void run('hold', () => holdItem(slug, kind, id), `Holding · ${s.detail}`)}>{s.title}</Button>;
    }
    if (s.id === 'compensating' && s.status === 'unlocked') {
      const n = batchIds?.length ?? 0;
      return <Button compact id="comp-open" onClick={() => setCompOpen(true)}>{n > 1 ? `Request ${n} compensating reviews…` : 'Request compensating review…'}</Button>;
    }
    return null;
  };

  return (
    <div className="gp-form" id="sod-state" tabIndex={-1}>
      <div className="gp-state gp-state--warn">
        <strong>Nobody else can approve this right now</strong>
        <span className="gp-dim">{(a.route.reason_text ?? a.route.reasons).join(' · ') || 'You prepared it, so you can’t approve it alone.'}{a.hard_date ? ` · hard date ${fmtDay(a.hard_date)}` : ''}</span>
      </div>
      {a.options.add_independent_party.recommended ? (
        <div className="gp-panel">
          <p>{a.options.add_independent_party.note}</p>
          <div className="gp-actions">
            {onAddAdvisor
              ? <Button variant="primary" id="sod-add-advisor" onClick={(e) => onAddAdvisor(e.currentTarget)}>Add an advisor seat</Button>
              : <AskAdmin slug={slug} compact verb="add an advisor seat" what="adds advisor seats" subject="GAC Pro: we need an advisor seat (independent party)" />}
          </div>
        </div>
      ) : null}
      {error ? <Alert variant="error">{error}</Alert> : null}
      <ol className="gp-alts" aria-label="Options, in order">
        {steps.map((s) => (
          <li key={s.id} id={`alt-${s.id}`} tabIndex={-1} data-step={s.id} data-status={s.status}>
            <div className="gp-ow-line"><strong>{s.title}</strong><Chip text={STATUS_CHIP[s.status].text} tone={STATUS_CHIP[s.status].tone} /></div>
            <div className="gp-dim">{s.detail}</div>
            {action(s) ? <div className="gp-actions">{action(s)}</div> : null}
          </li>
        ))}
      </ol>
      {a.compensating_allowed ? (
        <CompensatingDialog open={compOpen} slug={slug} kind={kind} ids={batchIds?.length ? batchIds : [id]} names={names}
          tried={alternativesTried(a, names)} onClose={() => setCompOpen(false)}
          onDone={(m) => { setCompOpen(false); void qc.invalidateQueries({ queryKey: ['today', slug] }); onDone(m, 'sod-state'); }} />
      ) : null}
    </div>
  );
}

export function CompensatingDialog({ open, slug, kind, ids, names, tried, onClose, onDone }: {
  open: boolean; slug: string; kind: CoverageItemKind; ids: string[]; names: Names; tried: string[]; onClose: () => void; onDone: (msg: string) => void;
}) {
  const [facts, setFacts] = useState('');
  const [alts, setAlts] = useState<string[]>(tried);
  const [other, setOther] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dry, setDry] = useState<CompensatingDryRun | null>(null);
  const [checker, setChecker] = useState('');
  const [due, setDue] = useState('');
  const [party, setParty] = useState({ name: '', email: '' });
  const today = new Date().toISOString().slice(0, 10);
  const maxDue = new Date(Date.now() + CHECK_MAX_DAYS * 86_400_000).toISOString().slice(0, 10);
  const clear = (k: string) => { setError(null); if (errors[k]) setErrors((x) => ({ ...x, [k]: '' })); };
  const list = () => [...alts, ...(other.trim() ? [other.trim()] : [])];

  // Step 1 → the dry run (nothing written): facts rule, alternatives, who could check.
  const step1 = async () => {
    const e: Record<string, string> = {};
    const t = facts.trim();
    if (t.length < FACTS_MIN) e.facts = `At least ${FACTS_MIN} characters: who was unavailable and why no one else could approve before the hard date. ${t.length}/${FACTS_MIN}.`;
    if (!list().length) e.alts = 'Tick or add at least one alternative you tried.';
    setErrors(e);
    if (Object.values(e).some(Boolean)) return false;
    setBusy(true); setError(null);
    try {
      const runs = await Promise.all(ids.map((itemId) => compensatingDryRun(slug, { item_kind: kind, item_id: itemId, facts: t, alternatives_tried: list() })));
      const bad = runs.map((r) => r.dry_run).find((d) => d.facts && !d.facts.ok);
      if (bad && bad.facts && !bad.facts.ok) { setErrors({ facts: bad.facts.message }); return false; }
      const blocked = runs.map((r) => r.dry_run).find((d) => !d.is_preparer || !d.alternatives.compensating_allowed);
      if (blocked) { setError((blocked.reason_text ?? blocked.reasons).join(' ') || 'A compensating review isn’t possible for this item right now.'); return false; }
      setDry(runs[0]!.dry_run);
      return false;
    } catch (err) { setError(errorText(err, 'Eligibility could not be checked. Try again.')); return false; }
    finally { setBusy(false); }
  };
  // Step 2 → ONE submit per item (resumes a pending review instead of creating a second).
  const step2 = async () => {
    const e: Record<string, string> = {};
    if (!checker) e.checker = 'Choose who checks it.';
    if (!due) e.due = 'Choose a check-by date.';
    else if (due < today || due > maxDue) e.due = `Within ${CHECK_MAX_DAYS} days (by ${fmtDay(maxDue)}).`;
    setErrors(e);
    if (Object.values(e).some(Boolean)) return false;
    setBusy(true); setError(null);
    try {
      const batch = ids.length > 1 ? `batch-${Date.now().toString(36)}` : undefined;
      for (const itemId of ids) {
        const r = await submitCompensating(slug, {
          item_kind: kind, item_id: itemId, facts: facts.trim(), alternatives_tried: list(), batch_id: batch, checker_user_id: checker, due_at: `${due}T23:59:59.000Z`,
        });
        if (r.status !== 'self_approved') { setError(r.prompt); return false; }
      }
      onDone(`Approved with a compensating review${ids.length > 1 ? ` · ${ids.length} items` : ''} · ${nameOf(names, checker)} checks it by ${fmtDay(due)}`);
      return true;
    } catch (err) { setError(errorText(err, 'The approval could not be recorded.')); return false; }
    finally { setBusy(false); }
  };
  const addParty = async () => {
    const e: Record<string, string> = {};
    if (!party.name.trim()) e.partyName = 'Enter their name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(party.email.trim())) e.partyEmail = 'Enter a work email.';
    setErrors(e);
    if (Object.values(e).some(Boolean)) return false;
    setBusy(true); setError(null);
    try {
      const r = await addIndependentParty(slug, { kind: 'advisor_seat', contact_name: party.name.trim(), contact_email: party.email.trim() });
      // Truthful (S6): the seat counts once they accept; the review waits for them, nothing is recorded yet.
      onDone(r.state === 'active' ? `${party.name.trim()} fills the seat · request the review again to choose them`
        : `${r.invite?.state === 'sent' ? 'Invite sent' : 'Seat added · share the invite link'} · ${party.name.trim()} can check it once they accept · nothing is recorded until then`);
      return true;
    } catch (err) { setError(errorText(err, 'The independent party could not be added.')); return false; }
    finally { setBusy(false); }
  };

  const checkers: CheckerOptions | null = dry?.checkers ?? null;
  const needsParty = checkers?.status === 'needs_independent_party';
  const noun = kind === 'signature_request' ? 'signature' : 'item';
  const title = ids.length > 1 ? `Compensating review · ${ids.length} ${noun}s` : 'Compensating review';
  const stepInfo = !dry ? { n: 1, of: 2, label: 'What happened' } : { n: 2, of: 2, label: needsParty ? 'Add someone to check it' : 'Who checks it' };
  return (
    <ActionDialog open={open} title={title} busy={busy} error={error} onClose={onClose} step={stepInfo}
      submitLabel={!dry ? 'Continue' : needsParty ? 'Add independent party' : 'Approve · record compensating review'}
      onSubmit={!dry ? step1 : needsParty ? addParty : step2}>
      {!dry ? (
        <>
          <Field id="comp-facts" label="Facts" required hint={`At least ${FACTS_MIN} characters · who was unavailable and why no one else could approve. Deadline pressure alone isn’t a reason. ${facts.trim().length}/${FACTS_MIN}`} error={errors.facts}>
            <Textarea rows={4} maxLength={4000} value={facts} onChange={(e) => { setFacts(e.currentTarget.value); clear('facts'); }} />
          </Field>
          <fieldset className="gp-fieldset" aria-describedby={errors.alts ? 'comp-alts-err' : undefined} aria-invalid={errors.alts ? true : undefined}>
            <legend>Alternatives tried</legend>
            {tried.map((t, i) => (
              <Checkbox key={t} id={`comp-alt-${i}`} label={t} checked={alts.includes(t)}
                onChange={(e) => { const on = e.currentTarget.checked; setAlts((x) => (on ? [...x, t] : x.filter((y) => y !== t))); clear('alts'); }} />
            ))}
            <Field id="comp-alt-other" label="Something else you tried" hint="Optional · 3–500 characters">
              <Input value={other} maxLength={500} onChange={(e) => { setOther(e.currentTarget.value); clear('alts'); }} />
            </Field>
            {errors.alts ? <p id="comp-alts-err" role="alert" className="gp-field-err">{errors.alts}</p> : null}
          </fieldset>
          <p className="gp-dim">Listed as a known SoD limitation · your auditor decides how it’s reported.</p>
        </>
      ) : needsParty ? (
        <>
          <Alert variant="warning">{checkers!.status === 'needs_independent_party' ? checkers!.prompt : ''}</Alert>
          <IneligibleList checkers={checkers!} names={names} />
          <Field id="party-name" label="Independent party · name" required error={errors.partyName}>
            <Input value={party.name} maxLength={200} onChange={(e) => { setParty((p) => ({ ...p, name: e.currentTarget.value })); clear('partyName'); }} />
          </Field>
          <Field id="party-email" label="Work email" required hint="Outside your reporting line · never your audit firm" error={errors.partyEmail}>
            <Input type="email" value={party.email} maxLength={320} onChange={(e) => { setParty((p) => ({ ...p, email: e.currentTarget.value })); clear('partyEmail'); }} />
          </Field>
        </>
      ) : (
        <>
          {dry.pending_comp_review_id ? <p className="gp-dim">A review for this item was already started — this finishes it.</p> : null}
          <Field id="comp-checker" label="Independent check by" required hint="Outside your reporting line and not in a conflicting pair" error={errors.checker}>
            <Select value={checker} onChange={(e) => { setChecker(e.currentTarget.value); clear('checker'); }}>
              <option value="">Choose…</option>
              {checkers!.checkers.map((c) => (
                <option key={c.user_id} value={c.user_id} disabled={!c.eligible}>
                  {`${nameOf(names, c.user_id)}${c.eligible ? '' : ` · not eligible · ${checkerWhy(c.why)}`}`}
                </option>
              ))}
            </Select>
          </Field>
          <Field id="comp-due" label="Check by" required hint={`Within ${CHECK_MAX_DAYS} days`} error={errors.due}>
            <Input type="date" min={today} max={maxDue} value={due} onChange={(e) => { setDue(e.currentTarget.value); clear('due'); }} />
          </Field>
        </>
      )}
    </ActionDialog>
  );
}

function IneligibleList({ checkers, names }: { checkers: CheckerOptions; names: Names }) {
  return (
    <ul className="gp-plain-list" aria-label="Why nobody here is eligible">
      {checkers.checkers.map((c) => <li key={c.user_id}>{`${nameOf(names, c.user_id)} · ${checkerWhy(c.why)}`}</li>)}
    </ul>
  );
}
